import { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';

export const OfflineIndicator = () => {
  const [online, setOnline] = useState(navigator.onLine);
  const [showReconnect, setShowReconnect] = useState(false);
  const [queueCount, setQueueCount] = useState(0);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      setShowReconnect(true);
      // Sync queued actions
      syncOfflineQueue().then(count => {
        if (count > 0) setQueueCount(0);
      });
      setTimeout(() => setShowReconnect(false), 3000);
    };
    const goOffline = () => {
      setOnline(false);
      setShowReconnect(false);
    };

    // Check queue
    const queue = JSON.parse(localStorage.getItem('offline-queue') || '[]');
    setQueueCount(queue.length);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (online && !showReconnect) return null;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[60] flex items-center justify-center gap-2 py-2 text-xs font-medium transition-all ${
        online
          ? 'bg-emerald-500 text-white'
          : 'bg-amber-500 text-white'
      }`}
      data-testid="offline-indicator"
    >
      {online ? (
        <>
          <Wifi className="w-3.5 h-3.5" />
          Conexion restaurada
          {queueCount > 0 && (
            <span className="flex items-center gap-1 ml-1">
              <RefreshCw className="w-3 h-3 animate-spin" />
              Sincronizando {queueCount} acciones...
            </span>
          )}
        </>
      ) : (
        <>
          <WifiOff className="w-3.5 h-3.5" />
          Sin conexion - Los cambios se guardaran localmente
        </>
      )}
    </div>
  );
};

// Offline queue management
export function queueOfflineAction(action) {
  const queue = JSON.parse(localStorage.getItem('offline-queue') || '[]');
  queue.push({
    ...action,
    id: `offline_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    timestamp: new Date().toISOString(),
  });
  localStorage.setItem('offline-queue', JSON.stringify(queue));
  return queue.length;
}

export async function syncOfflineQueue() {
  const queue = JSON.parse(localStorage.getItem('offline-queue') || '[]');
  if (queue.length === 0) return 0;

  const { default: api } = await import('../../lib/api');
  let synced = 0;

  for (const action of queue) {
    try {
      if (action.method === 'POST') {
        await api.post(action.url, action.data);
      } else if (action.method === 'PATCH') {
        await api.patch(action.url, action.data);
      }
      synced++;
    } catch (err) {
      console.error('Failed to sync action:', action, err);
      // Keep failed actions in queue for next sync
      break;
    }
  }

  if (synced > 0) {
    const remaining = queue.slice(synced);
    localStorage.setItem('offline-queue', JSON.stringify(remaining));
  }

  return synced;
}
